import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import DocumentGenerationPanel from '../../components/DocumentGenerationPanel';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  addGeneratedDraft,
  filterDiagnosticCollections,
  filterDiagnosticValues,
  formatWorkspaceValue,
  getDocumentDiagnostics,
  getWorkspaceValueAtPath,
  humanizeWorkspaceName,
  isBlankWorkspaceValue,
  previewMessage,
  selectGeneratedDraft,
  sortGeneratedDocuments
} from '../../lib/documentWorkspace';

const formatSupabaseError = (error, fallback) =>
  [error?.message, error?.details, error?.hint].filter(Boolean).join(' ') || fallback;

const StructuredValue = ({ value, depth = 0 }) => {
  if (isBlankWorkspaceValue(value) || depth > 8) return <span className="italic text-slate-500">No value supplied</span>;
  if (!Array.isArray(value) && typeof value !== 'object') return <span>{formatWorkspaceValue(value)}</span>;
  if (Array.isArray(value)) return <div className="space-y-2">{value.map((item, index) => <div key={index} className="rounded border border-slate-200 bg-white p-2"><p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Item {index + 1}</p><StructuredValue value={item} depth={depth + 1} /></div>)}</div>;
  return <dl className="space-y-2">{Object.entries(value).map(([key, item]) => <div key={key} className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-2"><dt className="text-xs font-medium text-slate-500 break-words">{humanizeWorkspaceName(key)}</dt><dd className="text-sm text-slate-700 break-words"><StructuredValue value={item} depth={depth + 1} /></dd></div>)}</dl>;
};

const DocumentWorkspace = () => {
  const { matterId } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const previewRef = useRef(null);
  const [matter, setMatter] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [blanksOnly, setBlanksOnly] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [expandedPreview, setExpandedPreview] = useState(false);
  const [rawCollections, setRawCollections] = useState({});

  const loadWorkspace = async (preferredId) => {
    if (!profile?.law_firm_id || !matterId) return;
    try {
      setLoading(true);
      setError(null);
      const [matterRes, documentsRes] = await Promise.all([
        supabase
          .from('matters')
          .select('*, client:clients(*)')
          .eq('id', matterId)
          .eq('law_firm_id', profile.law_firm_id)
          .single(),
        supabase
          .from('generated_documents')
          .select('*, template:document_templates(name, version)')
          .eq('matter_id', matterId)
          .order('created_at', { ascending: false })
      ]);
      if (matterRes.error) throw matterRes.error;
      if (documentsRes.error) throw documentsRes.error;
      const drafts = sortGeneratedDocuments(documentsRes.data || []);
      setMatter(matterRes.data);
      setDocuments(drafts);
      setSelectedId(selectGeneratedDraft(drafts, preferredId || selectedId)?.id || '');
    } catch (loadError) {
      setError(formatSupabaseError(loadError, 'Unable to load the document workspace.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkspace();
  }, [matterId, profile?.law_firm_id]);

  const selectedDocument = useMemo(
    () => selectGeneratedDraft(documents, selectedId),
    [documents, selectedId]
  );
  const diagnostics = useMemo(() => getDocumentDiagnostics(selectedDocument), [selectedDocument]);
  const values = useMemo(
    () => filterDiagnosticValues(diagnostics, search, blanksOnly),
    [diagnostics, search, blanksOnly]
  );
  const collectionValues = useMemo(
    () => filterDiagnosticCollections(diagnostics, selectedDocument?.variables_json || {}, search, blanksOnly),
    [diagnostics, selectedDocument, search, blanksOnly]
  );
  const blankNames = useMemo(() => new Set(diagnostics.blank_variables || []), [diagnostics]);

  const loadPreview = async () => {
    if (!selectedDocument?.storage_bucket || !selectedDocument?.storage_path || !previewRef.current) return;
    let active = true;
    try {
      setPreviewLoading(true);
      setPreviewError(null);
      previewRef.current.replaceChildren();
      const { data, error: signedUrlError } = await supabase.storage
        .from(selectedDocument.storage_bucket)
        .createSignedUrl(selectedDocument.storage_path, 60);
      if (signedUrlError) throw signedUrlError;
      const response = await fetch(data.signedUrl);
      if (!response.ok) throw new Error(`Unable to retrieve DOCX preview (${response.status}).`);
      const blob = await response.blob();
      const { renderAsync } = await import('docx-preview');
      if (!active || !previewRef.current) return;
      await renderAsync(blob, previewRef.current, undefined, {
        inWrapper: true,
        breakPages: true,
        renderHeaders: true,
        renderFooters: true,
        ignoreLastRenderedPageBreak: false
      });
    } catch (renderError) {
      if (active) setPreviewError(previewMessage(renderError));
    } finally {
      if (active) setPreviewLoading(false);
    }
    return () => { active = false; };
  };

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      if (!selectedDocument?.storage_path || !previewRef.current) return;
      try {
        setPreviewLoading(true);
        setPreviewError(null);
        previewRef.current.replaceChildren();
        const { data, error: signedUrlError } = await supabase.storage
          .from(selectedDocument.storage_bucket)
          .createSignedUrl(selectedDocument.storage_path, 60);
        if (signedUrlError) throw signedUrlError;
        const response = await fetch(data.signedUrl);
        if (!response.ok) throw new Error(`Unable to retrieve DOCX preview (${response.status}).`);
        const blob = await response.blob();
        const { renderAsync } = await import('docx-preview');
        if (cancelled || !previewRef.current) return;
        await renderAsync(blob, previewRef.current, undefined, {
            inWrapper: true, breakPages: true, renderHeaders: true, renderFooters: true, ignoreLastRenderedPageBreak: false
        });
      } catch (renderError) {
        if (!cancelled) setPreviewError(previewMessage(renderError));
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    render();
    return () => { cancelled = true; };
  }, [selectedDocument?.id]);

  const downloadSelected = async () => {
    if (!selectedDocument?.storage_bucket || !selectedDocument?.storage_path) return;
    try {
      setPreviewError(null);
      const { data, error: signedUrlError } = await supabase.storage
        .from(selectedDocument.storage_bucket)
        .createSignedUrl(selectedDocument.storage_path, 60);
      if (signedUrlError) throw signedUrlError;
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (downloadError) {
      setPreviewError(formatSupabaseError(downloadError, 'Unable to download this DOCX draft.'));
    }
  };

  const handleGenerated = async (generated) => {
    setMatter((current) => current ? { ...current, status: 'documents_generated' } : current);
    await loadWorkspace(generated?.id);
  };

  if (!profile?.law_firm_id) {
    return <div className="p-8 text-center bg-white rounded-lg border border-slate-200">Law firm access is required.</div>;
  }

  if (loading) {
    return <div className="flex flex-col items-center justify-center py-20 text-slate-500"><SafeIcon icon={FiIcons.FiLoader} className="h-8 w-8 animate-spin mb-3" />Loading document workspace...</div>;
  }

  return (
    <div className="max-w-[1600px] mx-auto space-y-6 pb-12">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center">
          <button onClick={() => navigate(`/law-firm/matters/${matterId}`)} className="mr-4 p-2 rounded-full hover:bg-white text-slate-400"><SafeIcon icon={FiIcons.FiArrowLeft} className="h-6 w-6" /></button>
          <div><h1 className="text-2xl font-bold text-slate-900">Document Workspace</h1><p className="text-sm text-slate-500 mt-1">{matter?.client?.first_name} {matter?.client?.last_name} — compare merge data with a DOCX draft.</p></div>
        </div>
        <button onClick={() => loadWorkspace(selectedId)} className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"><SafeIcon icon={FiIcons.FiRefreshCw} className="mr-2 h-4 w-4" />Refresh drafts</button>
        <button onClick={() => navigate('/law-firm/templates')} className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md bg-slate-800 text-white hover:bg-slate-900"><SafeIcon icon={FiIcons.FiSettings} className="mr-2 h-4 w-4" />Manage templates</button>
      </div>

      {error && <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">{error}</div>}
      {matter && <DocumentGenerationPanel matter={matter} onDocumentGenerated={handleGenerated} showHistory={false} />}

      <div className={expandedPreview ? 'fixed inset-4 z-50 bg-slate-100 p-4 overflow-hidden rounded-xl shadow-2xl' : ''}>
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(320px,0.8fr)_minmax(0,1.7fr)] gap-6 xl:h-[calc(100vh-13rem)]">
          <aside className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col min-h-[560px]">
            <div className="p-5 border-b border-slate-100 space-y-4">
              <div className="flex items-center justify-between"><h2 className="font-bold text-slate-800">Merge data</h2><span className="text-xs text-slate-500">{diagnostics.flattened_variables?.length || 0} values · {diagnostics.blank_variables?.length || 0} blank</span></div>
              <select value={selectedDocument?.id || ''} onChange={(event) => setSelectedId(event.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white">
                {documents.map((document) => <option key={document.id} value={document.id}>{document.file_name || document.template?.name || 'Generated draft'} — {format(new Date(document.created_at), 'MMM d, h:mm a')}</option>)}
              </select>
              {!selectedDocument && <p className="text-sm text-slate-500">No generated drafts are available for this matter yet.</p>}
              {selectedDocument && <div className="text-xs text-slate-500 space-y-1"><p>Template: <span className="font-medium text-slate-700">{selectedDocument.template?.name || 'Template unavailable'}{selectedDocument.template?.version ? ` v${selectedDocument.template.version}` : ''}</span></p><p>Generated {format(new Date(selectedDocument.completed_at || selectedDocument.created_at), 'MMM d, yyyy h:mm a')}</p>{diagnostics.is_legacy_fallback && <p className="text-amber-700">Using merge-data fallback for an older draft.</p>}</div>}
              {diagnostics.structured_scalar_tags?.length > 0 && <details className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"><summary className="cursor-pointer font-medium">Structured scalar tags were rendered blank</summary><ul className="mt-2 space-y-1">{diagnostics.structured_scalar_tags.map((tag) => <li key={tag.tag}><code>{`{${tag.tag}}`}</code> — {tag.value_type}{tag.item_count !== undefined ? ` (${tag.item_count} items)` : ''}</li>)}</ul></details>}
              <div className="relative"><input value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }} placeholder="Search names and values..." className="w-full border border-slate-300 rounded-md px-3 py-2 pr-14 text-sm" />{search && <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-500 hover:text-slate-800">Clear</button>}</div>
              <label className="flex items-center text-sm text-slate-600"><input type="checkbox" checked={blanksOnly} onChange={(event) => setBlanksOnly(event.target.checked)} className="mr-2" />Show blank values only</label>
            </div>
            <div className="p-5 overflow-y-auto space-y-3">
              {collectionValues.map((collection) => {
                const collectionValue = getWorkspaceValueAtPath(selectedDocument?.variables_json || {}, collection.name);
                const rawVisible = rawCollections[collection.name];
                return <details key={collection.name} className="border border-blue-100 rounded-md bg-blue-50/50 p-3"><summary className="cursor-pointer text-sm font-semibold text-blue-900">{humanizeWorkspaceName(collection.name)} <span className="font-normal text-blue-700">— {collection.item_count === 0 ? 'Collection is empty' : `${collection.item_count} item${collection.item_count === 1 ? '' : 's'}`}</span></summary><div className="mt-3 space-y-3">{Array.isArray(collectionValue) ? <StructuredValue value={collectionValue} /> : <span className="text-sm italic text-slate-500">No collection data was recorded for this draft.</span>}<button type="button" onClick={() => setRawCollections((current) => ({ ...current, [collection.name]: !rawVisible }))} className="text-xs font-medium text-blue-700 hover:text-blue-900">{rawVisible ? 'Hide raw data' : 'Show raw data'}</button>{rawVisible && <pre className="overflow-auto rounded bg-slate-900 p-3 text-xs whitespace-pre-wrap text-slate-100">{JSON.stringify(collectionValue, null, 2)}</pre>}</div></details>;
              })}
              {values.map((item) => {
                const blank = blankNames.has(item.name) || isBlankWorkspaceValue(item.value);
                return <div key={item.name} className={`rounded-md border p-3 ${blank ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'}`}><p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 break-all">{humanizeWorkspaceName(item.name)}</p><p className="mt-1 text-[10px] text-slate-400 break-all">{item.name}</p><p className={`mt-1 text-sm whitespace-pre-wrap break-words ${blank ? 'text-amber-800 italic' : 'text-slate-700'}`}>{blank ? 'No value supplied' : <StructuredValue value={item.value} />}</p></div>;
              })}
              {values.length === 0 && collectionValues.length === 0 && <p className="text-sm text-slate-500">No merge values match this filter.</p>}
            </div>
          </aside>

          <section className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col min-h-[620px]">
            <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"><div><h2 className="font-bold text-slate-800">{selectedDocument?.file_name || 'Document preview'}</h2><p className="text-xs text-slate-500 mt-1">The downloadable DOCX is the authoritative editable draft.</p></div><div className="flex gap-2"><button onClick={loadPreview} disabled={!selectedDocument || previewLoading} className="px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-md border border-slate-300 text-slate-700 disabled:opacity-50">Retry preview</button><button onClick={downloadSelected} disabled={!selectedDocument} className="px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-md bg-slate-800 text-white disabled:opacity-50"><SafeIcon icon={FiIcons.FiDownload} className="inline mr-1 h-3.5 w-3.5" />DOCX</button><button onClick={() => setExpandedPreview((value) => !value)} className="px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-md border border-slate-300 text-slate-700">{expandedPreview ? 'Close expanded' : 'Expand'}</button></div></div>
            <div className="relative flex-1 overflow-auto bg-slate-100 p-4">
              {previewLoading && <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-100/80 text-slate-500"><SafeIcon icon={FiIcons.FiLoader} className="mr-2 h-5 w-5 animate-spin" />Rendering DOCX preview...</div>}
              {previewError && <div className="mb-4 border border-red-200 bg-red-50 rounded-md p-4 text-sm text-red-700">{previewError}<p className="mt-2 text-xs">You can still download the DOCX draft.</p></div>}
              {!selectedDocument && <div className="h-full flex items-center justify-center text-sm text-slate-500">Generate or select a draft to preview it here.</div>}
              <div ref={previewRef} className="document-preview text-slate-900" />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DocumentWorkspace;
